import Vue from 'vue'
import _ from 'lodash'
import {
    checkIsValidDocumentTax,
    checkIsValidExpenseSplitLines,
    checkIsValidLinesTotalSum,
    checkIsValidPurchaseLineTotal,
    checkIsValidPurchaseTotal,
    checkIsValidPurchaseTotalAsSumByLines,
    checkIsValidStatementLineBalance,
    checkStatementLineHasDebitOrCredit
} from '@/utils/document/validations/custom'
import {checkIsValidFormField} from '@/utils/document/validations/generic'
import {
    FieldValidationLevels,
    FieldValidations,
    getFieldValidationInfo
} from '@/types/document-viewer/field/modules/validation'
import {getFieldPathMeta} from '@/types/document-viewer/field/modules/path'
import {checkIsNewLine} from '@/utils/document'
import {
    FormExtensionType,
    getFormExtensionByName,
    getFormExtensionsList,
    validationExtensionReducer
} from '@/types/document-viewer/extension'
import {MatchingExtensionProcessLevel} from '@/types/document-viewer/extension/matching'
import {AppDocLineTypes} from '@/types/document/lines'
import {$lang} from '@/views/settings/team/lang'
import {getFormFieldMetadataIdx, getFormFieldValidationPath} from '@/types/document-viewer/field/modules/metadata'
import {checkIsValidRequiredIfValidation} from '@/utils/document/validations/required-if'

export const DOC_METADATA_SET = 'DOC_METADATA_SET'
export const DOC_METADATA_RESET = 'DOC_METADATA_RESET'
export const DOC_METADATA_TOUCH = 'DOC_METADATA_TOUCH'
export const DOC_METADATA_TOUCH_FIELD = 'DOC_METADATA_TOUCH_FIELD'
export const DOC_METADATA_TOUCH_FIELDS_BATCH = 'DOC_METADATA_TOUCH_FIELDS_BATCH'
export const DOC_METADATA_VALIDATE_FORM = 'DOC_METADATA_VALIDATE_FORM'
export const DOC_METADATA_VALIDATE_FIELDS_BATCH = 'DOC_METADATA_VALIDATE_FIELDS_BATCH'
export const DOC_METADATA_GET_DIFF_AFTER_FIELD_VALIDATION = 'DOC_METADATA_GET_DIFF_AFTER_FIELD_VALIDATION'
export const DOC_METADATA_CHECK_IS_VALID_GROUP_VALIDATION = 'DOC_METADATA_CHECK_IS_VALID_GROUP_VALIDATION'
export const DOC_METADATA_BUILD_ERRORS_LIST = 'DOC_METADATA_BUILD_ERRORS_LIST'
export const DOC_METADATA_BUILD_WARNINGS_LIST = 'DOC_METADATA_BUILD_WARNINGS_LIST'
export const DOC_METADATA_UPDATE_FIELDS_BATCH = 'DOC_METADATA_UPDATE_FIELDS_BATCH'
export const DOC_METADATA_DELETE_PROP = 'DOC_METADATA_DELETE_PROP'
export const DOC_METADATA_REMOVE_FOR_LINE = 'DOC_METADATA_REMOVE_FOR_LINE'
export const DOC_METADATA_GENERATE_FROM_SETTINGS = 'DOC_METADATA_GENERATE_FROM_SETTINGS'
export const DOC_METADATA_GENERATE_FOR_LINE = 'DOC_METADATA_GENERATE_FOR_LINE'
export const DOC_METADATA_SET_IS_TOUCHED = 'DOC_METADATA_SET_IS_TOUCHED'
export const DOC_METADATA_SET_IS_VALID = 'DOC_METADATA_SET_IS_VALID'
export const DOC_METADATA_SET_ERRORS_LIST = 'DOC_METADATA_SET_ERRORS_LIST'
export const DOC_METADATA_SET_WARNINGS_LIST = 'DOC_METADATA_SET_WARNINGS_LIST'

export const state = {
    formMetadata: [], // Array<FieldMetadata>,

    isTouchedForm: false,
    isValidForm: false,
    errorsList: [],
    warningsList: []
}

export const getters = {
    formMetadata: state => state.formMetadata || [],

    // path: FormField.path, idx for array fields (like lines)
    getFieldMetadata: (state, getters) => ({path, idx}) => {
        const metadataIdx = getFormFieldMetadataIdx({formMetadata: getters.formMetadata, path, idx})
        return metadataIdx !== -1 ? getters.formMetadata[metadataIdx] : null
    },

    getFieldValidations: (state, getters) => ({path, idx}) => {
        const metadata = getters.getFieldMetadata({path, idx})
        return metadata && _.isArray(metadata.validations) ? metadata.validations : []
    },

    getFieldFailedValidations: (state, getters) => ({path, idx}) => {
        return getters.getFieldValidations({path, idx}).filter(v => !v.valid)
    },

    isTouchedForm: state => state.isTouchedForm,
    isValidForm: state => state.isValidForm,
    errorsList: state => state.errorsList,
    warningsList: state => state.warningsList
}

export const mutations = {
    [DOC_METADATA_SET]: (state, data) => {
        state.formMetadata = _.cloneDeep(data)
    },
    [DOC_METADATA_UPDATE_FIELDS_BATCH]: (state, fields) => {
        const uniqUpdates = _.uniqWith(fields, _.isEqual)
        for (const {metadataIdx, path, data} of uniqUpdates) {
            if (!state.formMetadata[metadataIdx]) {
                continue
            }
            Vue.set(state.formMetadata, metadataIdx, _.set(_.cloneDeep(state.formMetadata[metadataIdx]), path, data))
        }
    },
    [DOC_METADATA_DELETE_PROP]: (state, {metadataIdx}) => {
        if (state.formMetadata[metadataIdx]) {
            Vue.delete(state.formMetadata, metadataIdx)
        }
    },
    [DOC_METADATA_SET_IS_TOUCHED]: (state, val) => {
        state.isTouchedForm = val
    },
    [DOC_METADATA_SET_IS_VALID]: (state, val) => {
        state.isValidForm = val
    },
    [DOC_METADATA_SET_ERRORS_LIST]: (state, list) => {
        state.errorsList = list
    },
    [DOC_METADATA_SET_WARNINGS_LIST]: (state, list) => {
        state.warningsList = list
    }
}

export const actions = {
    [DOC_METADATA_DELETE_PROP]: ({commit, getters, dispatch}, {path, idx}) => {
        const metadataIdx = getFormFieldMetadataIdx({formMetadata: getters.formMetadata, path, idx})
        if (metadataIdx !== -1) {
            commit(DOC_METADATA_DELETE_PROP, {metadataIdx})
        }
    },

    [DOC_METADATA_GET_DIFF_AFTER_FIELD_VALIDATION]: async({dispatch, rootGetters, getters, commit}, {path, idx}) => {
        const {prop, isLine, isLandedCostLine, isCustom} = getFieldPathMeta(path)

        const isExistingLineSectionField = (isLine || isLandedCostLine) && _.isNumber(idx) && rootGetters['doc_viewer/lines'][idx]
        if (isExistingLineSectionField) {
            // NOTE: this is an extra check/replacement for landed cost line type
            // (added here in order to omit check on every line update action)
            const linesProp = rootGetters['doc_viewer/lines'][idx].type === AppDocLineTypes.LANDED_COST ? 'lcLines' : 'lines'
            path = isCustom ? `${linesProp}.custom.${prop}` : `${linesProp}.${prop}`
        }
        const fieldMetadata = _.cloneDeep(getters.getFieldMetadata({path, idx}))
        if (!fieldMetadata || !_.isArray(fieldMetadata.validations)) {
            return []
        }

        const metadataDiff = []

        for (const validation of fieldMetadata.validations) {
            // GROUP validations
            if (_.isArray(validation.group)) {
                const isValidGroup = await dispatch(DOC_METADATA_CHECK_IS_VALID_GROUP_VALIDATION, {
                    key: validation.key,
                    idx
                })

                for (const groupFieldPath of validation.group) {
                    const validationPath = getFormFieldValidationPath({
                        formMetadata: getters.formMetadata,
                        validationKey: validation.key,
                        path: groupFieldPath,
                        idx
                    })
                    if (!validationPath) {
                        continue
                    }
                    const {metadataIdx, validationIdx} = validationPath
                    const wasValid = Boolean(_.get(getters.formMetadata, [metadataIdx, 'validations', validationIdx, 'valid']))
                    if (wasValid !== isValidGroup) {
                        metadataDiff.push({
                            metadataIdx,
                            path: ['validations', validationIdx, 'valid'],
                            data: isValidGroup
                        })
                    }
                }
            } else { // SINGLE FIELD validation
                const validationPath = getFormFieldValidationPath({
                    formMetadata: getters.formMetadata, validationKey: validation.key, path, idx
                })
                if (!validationPath) {
                    continue
                }
                let isValid = true
                if (validation.key === FieldValidations.REQUIRED_IF) {
                    isValid = checkIsValidRequiredIfValidation({
                        field: rootGetters['doc_viewer_settings/getFieldSettingsByPath'](path),
                        fields: rootGetters['doc_viewer_settings/fields'],
                        idx,
                        params: validation.params,
                        doc: rootGetters['doc_viewer/docWithNormalizedParts']
                    })
                } else {
                    isValid = checkIsValidFormField({
                        key: validation.key,
                        params: validation.params,
                        value: rootGetters['doc_viewer/getFormFieldValue']({path, idx})
                    })
                }
                const {metadataIdx, validationIdx} = validationPath
                const wasValid = Boolean(_.get(getters.formMetadata, [metadataIdx, 'validations', validationIdx, 'valid']))
                if (wasValid !== isValid) {
                    metadataDiff.push({
                        metadataIdx,
                        path: ['validations', validationIdx, 'valid'],
                        data: isValid
                    })
                }
            }
        }
        return metadataDiff
    },

    /**
     * @param fields:
     *    Array<{
     *       path: FieldMetadata['path'] | FormField['path'],
     *       idx: FieldMetadata['idx'] | FormField['idx'],
     *    }>
     * */
    [DOC_METADATA_VALIDATE_FIELDS_BATCH]: async({dispatch, rootGetters, getters, commit}, fields) => {
        if (!_.isArray(fields) || fields.length === 0) {
            return
        }
        let metadataDiff = []
        let metadataDiffPart = []
        for (const {path, idx} of fields) {
            metadataDiffPart = await dispatch(DOC_METADATA_GET_DIFF_AFTER_FIELD_VALIDATION, {path, idx})
            metadataDiff = metadataDiff.concat(metadataDiffPart)
        }
        if (metadataDiff.length !== 0) {
            commit(DOC_METADATA_UPDATE_FIELDS_BATCH, metadataDiff)
            if (getters.isTouchedForm) {
                await dispatch(DOC_METADATA_BUILD_ERRORS_LIST)
            }
        }
    },

    [DOC_METADATA_VALIDATE_FORM]: async({rootGetters, getters, dispatch}) => {
        if (rootGetters['doc_viewer/disabledDocument']) {
            return
        }
        const allFields = getters.formMetadata.map(({idx, path}) => ({path, idx}))
        dispatch(DOC_METADATA_VALIDATE_FIELDS_BATCH, allFields)
    },

    /* It is easier to check group validations as a vuex action instead of an independent function
       (as it is relies on to the state of the document viewer)

        key - FieldValidations
        idx - for array fields like lines (for each array item created a separate validation group)
    */
    [DOC_METADATA_CHECK_IS_VALID_GROUP_VALIDATION]: ({rootGetters}, {key, idx}) => {
        const doc = rootGetters['doc_viewer/doc']
        const line = rootGetters['doc_viewer/lines'][idx] || {}
        const notDeletedLines = rootGetters['doc_viewer/notDeletedLines']
        const checkIsVisible = rootGetters['doc_viewer_settings/checkIsVisibleFieldByPath']

        if (key === FieldValidations.PURCHASE_TOTAL) {
            return checkIsValidPurchaseTotal({
                doc,
                withFreight: checkIsVisible('freight'),
                withRounding: checkIsVisible('rounding')
            })
        } else if (key === FieldValidations.PURCHASE_TOTAL_AS_SUM_BY_LINES) {
            return checkIsValidPurchaseTotalAsSumByLines({doc, lines: notDeletedLines})
        } else if (key === FieldValidations.LINES_TOTAL) {
            return checkIsValidLinesTotalSum({doc, lines: notDeletedLines})
        } else if (key === FieldValidations.EXPENSE_SPLIT) {
            return checkIsValidExpenseSplitLines({doc, lines: notDeletedLines})
        } else if (key === FieldValidations.PURCHASE_LINE_TOTAL) {
            return checkIsValidPurchaseLineTotal({doc, line})
        } else if (key === FieldValidations.STATEMENT_LINE_DEBIT_OR_CREDIT) {
            return checkStatementLineHasDebitOrCredit(line)
        } else if (key === FieldValidations.STATEMENT_LINE_BALANCE) {
            return checkIsValidStatementLineBalance(line)
        } else if (key === FieldValidations.DOCUMENT_TAX) {
            const useTaxRate = checkIsVisible('lines.taxType') && !checkIsVisible('lines.tax')
            const precision = rootGetters['doc_viewer_settings/getRoundingDecimalsByFieldPath']('lines.tax')
            return checkIsValidDocumentTax({
                doc,
                lines: notDeletedLines,
                useTaxRate,
                precision
            })
        } else if (key === FieldValidations.NO_NEW_LINE_ITEM) {
            return !!line.item_uuid
        } else if (key === FieldValidations.NO_NEW_CONTACT) {
            return !!rootGetters['doc_viewer/matchingSupplier']
        }
    },

    [DOC_METADATA_GENERATE_FROM_SETTINGS]: ({rootGetters}) => {
        const formMetadata = []
        let fieldMetadata
        let fieldMetadataValidations
        let fieldPathMeta
        const lines = rootGetters['doc_viewer/lines']

        const checkDocLineHasCorrectSection = (line, fieldPathMeta) => {
            return rootGetters['doc_viewer/isStatements'] ||
                (fieldPathMeta.isLine && line.type === AppDocLineTypes.ITEM) ||
                (fieldPathMeta.isLandedCostLine && line.type === AppDocLineTypes.LANDED_COST)
        }

        // 1. FROM FIELD SETTINGS
        for (const field of rootGetters['doc_viewer_settings/visibleFields']) {
            if (!_.isArray(field.validations)) {
                continue
            }
            fieldPathMeta = getFieldPathMeta(field.path)
            fieldMetadataValidations = field.validations.map(v => ({valid: true, ...v}))

            if (fieldPathMeta.isLine || fieldPathMeta.isLandedCostLine) {
                for (let idx = 0; idx < lines.length; idx++) {
                    let line = lines[idx]
                    if (!line || line.isDeleted || !checkDocLineHasCorrectSection(line, fieldPathMeta)) {
                        continue
                    }
                    fieldMetadata = {
                        path: field.path,
                        idx,
                        touched: false,
                        validations: fieldMetadataValidations
                    }
                    formMetadata.push(fieldMetadata)
                }
            } else {
                fieldMetadata = {
                    path: field.path,
                    touched: false,
                    validations: fieldMetadataValidations
                }
                formMetadata.push(fieldMetadata)
            }
        }

        // 2. FROM EXTENSIONS
        const validationExtensions = rootGetters['doc_viewer_settings/extensions']
            .filter(e => getFormExtensionsList().some(ext => ext.name === e.name && ext.type === FormExtensionType.VALIDATION))
            .filter(e => rootGetters['doc_viewer_settings/checkIsAvailableExtension'](e.name))

        for (const extension of validationExtensions) {
            const validationKey = validationExtensionReducer(extension.name)
            const extensionInfo = getFormExtensionByName(extension.name)
            if (!validationKey && (
                !extensionInfo ||
                !extensionInfo.available ||
                !_.isArray(extensionInfo.available.fields) ||
                extensionInfo.available.fields.length === 0)) {
                continue
            }
            let group = extensionInfo.available.fields

            const validation = {
                key: validationKey,
                level: FieldValidationLevels.ERROR,
                valid: true,
                group
            }

            let metadataIdx
            let vIdx
            let pathMeta
            for (const path of group) {
                if (!rootGetters['doc_viewer_settings/checkIsVisibleFieldByPath'](path)) {
                    continue
                }
                pathMeta = getFieldPathMeta(path)
                if (pathMeta.isLine || pathMeta.isLandedCostLine) {
                    for (let idx = 0; idx < lines.length; idx++) {
                        let line = lines[idx]
                        if (!line || line.isDeleted || !checkDocLineHasCorrectSection(line, fieldPathMeta)) {
                            continue
                        }
                        metadataIdx = formMetadata.findIndex(m => m.path === path && m.idx === idx)
                        if (metadataIdx === -1) {
                            formMetadata.push({
                                path,
                                idx,
                                touched: false,
                                validations: [validation]
                            })
                        } else {
                            vIdx = formMetadata[metadataIdx].validations.findIndex(v => v.key === validation.key)
                            if (vIdx === -1) {
                                formMetadata[metadataIdx].validations.push(validation)
                            }
                        }
                    }
                } else {
                    metadataIdx = formMetadata.findIndex(m => m.path === path)
                    if (metadataIdx === -1) {
                        formMetadata.push({
                            path,
                            touched: false,
                            validations: [validation]
                        })
                    } else {
                        formMetadata[metadataIdx].validations.push(validation)
                    }
                }
            }
        }
        return formMetadata
    },

    [DOC_METADATA_GENERATE_FOR_LINE]: async({commit, getters, dispatch, rootGetters}, {idx}) => {
        // NOTE: We use get full metadata function and then filter the result for new line
        // in order not to copy most of the code for line metadata generation
        const line = rootGetters['doc_viewer/lines'][idx]
        if (!line) {
            return
        }
        const isLandedCost = line.type === AppDocLineTypes.LANDED_COST
        const formMetadata = await dispatch(DOC_METADATA_GENERATE_FROM_SETTINGS) || []
        const lineFieldsMetadata = formMetadata.filter(m => m.idx === idx && getFieldPathMeta(m.path)[isLandedCost ? 'isLandedCost' : 'isLine'])
        commit(DOC_METADATA_SET, [...getters.formMetadata, ...lineFieldsMetadata])
    },

    [DOC_METADATA_REMOVE_FOR_LINE]: async({dispatch, getters, rootGetters}, {idx}) => {
        const line = rootGetters['doc_viewer/lines'][idx]
        if (!line) {
            return
        }
        const isLandedCost = line.type === AppDocLineTypes.LANDED_COST
        const formMetadata = _.cloneDeep(getters.formMetadata)
        for (const fieldMetadata of formMetadata) {
            if (fieldMetadata.idx === idx && getFieldPathMeta(fieldMetadata.path)[isLandedCost ? 'isLandedCost' : 'isLine']) {
                await dispatch(DOC_METADATA_DELETE_PROP, {path: fieldMetadata.path, idx: fieldMetadata.idx})
            }
        }
    },

    [DOC_METADATA_RESET]: ({commit}) => {
        commit(DOC_METADATA_SET, [])
        commit(DOC_METADATA_SET_IS_TOUCHED, false)
        commit(DOC_METADATA_SET_IS_VALID, true)
        commit(DOC_METADATA_SET_ERRORS_LIST, [])
        commit(DOC_METADATA_SET_WARNINGS_LIST, [])
    },

    [DOC_METADATA_TOUCH_FIELD]: async({commit, getters}, {path, idx}) => {
        const metadataIdx = getFormFieldMetadataIdx({formMetadata: getters.formMetadata, path, idx})
        if (metadataIdx === -1) {
            return
        }
        const metadataDiff = [{metadataIdx, path: 'touched', data: true}]
        commit(DOC_METADATA_UPDATE_FIELDS_BATCH, metadataDiff)
    },

    [DOC_METADATA_TOUCH_FIELDS_BATCH]: async({commit, getters}, fields) => {
        if (!_.isArray(fields) || fields.length === 0) {
            return
        }
        const metadataDiff = []
        for (const {idx, path} of fields) {
            const metadataIdx = getFormFieldMetadataIdx({formMetadata: getters.formMetadata, path, idx})
            if (metadataIdx === -1) {
                continue
            }
            metadataDiff.push({metadataIdx, path: 'touched', data: true})
        }
        commit(DOC_METADATA_UPDATE_FIELDS_BATCH, metadataDiff)
    },

    [DOC_METADATA_TOUCH]: async({commit, getters, dispatch}) => {
        await dispatch(DOC_METADATA_BUILD_ERRORS_LIST)
        const metadataDiff = getters.formMetadata.map((_, metadataIdx) => ({
            metadataIdx,
            path: 'touched',
            data: true
        }))
        commit(DOC_METADATA_UPDATE_FIELDS_BATCH, metadataDiff)
        commit(DOC_METADATA_SET_IS_TOUCHED, true)
    },

    [DOC_METADATA_BUILD_ERRORS_LIST]: ({commit, getters, rootGetters}) => {
        if (rootGetters['doc_viewer/disabledDocument']) {
            return
        }
        const errorsList = new Set()

        let settings
        let pathMeta
        let validationInfo
        let message
        for (const metadata of getters.formMetadata) {
            if (!_.isArray(metadata.validations) ||
                !rootGetters['doc_viewer_settings/checkIsVisibleFieldWithStateRulesApplied'](metadata.path)) {
                continue
            }
            settings = rootGetters['doc_viewer_settings/getFieldSettingsByPath'](metadata.path)
            pathMeta = getFieldPathMeta(metadata.path)
            for (const validation of metadata.validations.filter(v => !v.valid && v.level === FieldValidationLevels.ERROR)) {
                validationInfo = getFieldValidationInfo({key: validation.key, params: validation.params})
                if (!validationInfo) {
                    continue
                }
                message = ''
                if (pathMeta.isCompany) {
                    message += 'Company '
                } else if (pathMeta.isLine || pathMeta.isLandedCostLine) {
                    message += `Line ${metadata.idx + 1}: `
                }
                message += `${settings.label}: ${validationInfo.message}`
                errorsList.add(message)
            }
        }

        if (rootGetters['doc_viewer_settings/poCheckEnabled']) {
            if (rootGetters['doc_viewer_settings/poProcessLevel'] === MatchingExtensionProcessLevel.DOCUMENT) {
                if (rootGetters['doc_viewer_settings/poRequiredToProcess'] &&
                    rootGetters['doc_viewer/allocations'].length === 0) {
                    errorsList.add('At least one purchase order should be selected')
                }
                if (rootGetters['doc_viewer_po/hasTotalAllocatedGreaterThanDocTotal']) {
                    errorsList.add(`The total of all allocations cannot exceed the total of the document`)
                }
                if (rootGetters['doc_viewer_settings/poTotalDeviationCheckRequiredToProcess'] &&
                    rootGetters['doc_viewer_po/hasTotalUnallocatedGreaterThanAllowed']) {
                    errorsList.add(`Unallocated value is greater than allowed`)
                }
            } else { // Lines level
                if (rootGetters['doc_viewer_settings/poAllLinesShouldBeMatched'] &&
                    !rootGetters['doc_viewer_po/hasAllPoLinesMatched']) {
                    errorsList.add(`Not all purchase order lines were matched`)
                }
                if (rootGetters['doc_viewer_settings/poRequiredToProcess'] &&
                    !rootGetters['doc_viewer_po/purchaseOrder']) {
                    errorsList.add('Purchase order is not selected')
                }
            }
        }

        if (rootGetters['doc_viewer_settings/supplierReceiptsCheckEnabled']) {
            if (rootGetters['doc_viewer_settings/supplierReceiptsProcessLevel'] === MatchingExtensionProcessLevel.DOCUMENT) {
                if (rootGetters['doc_viewer_settings/supplierReceiptsTotalCheckRequiredToProcess'] &&
                    rootGetters['doc_viewer/allocations'].length === 0) {
                    errorsList.add(`At least one ${$lang.supplier.singular} receipt should be selected`)
                }
                if (rootGetters['doc_viewer_supplier_receipt/hasTotalAllocatedGreaterThanDocTotal']) {
                    errorsList.add(`The total of all allocations cannot exceed the total of the document`)
                }
                if (rootGetters['doc_viewer_settings/supplierReceiptsTotalCheckRequiredToProcess'] &&
                    rootGetters['doc_viewer_supplier_receipt/hasTotalUnallocatedGreaterThanAllowed']) {
                    errorsList.add(`Unallocated value is greater than allowed`)
                }
            } else { // Lines level
                if (rootGetters['doc_viewer_settings/supplierReceiptsRequiredToProcess'] &&
                    !rootGetters['doc_viewer_supplier_receipt/deliveryDocket']) {
                    errorsList.add(`${$lang.supplier.singular} receipt is not selected`)
                }
                if (rootGetters['doc_viewer_settings/supplierReceiptsAllLinesShouldBeMatched'] &&
                    !rootGetters['doc_viewer_supplier_receipt/hasAllDeliveryDocketLinesMatched']) {
                    errorsList.add(`Not all ${$lang.supplier.singular} receipt lines were matched`)
                }
            }
        }

        if (rootGetters['doc_viewer_settings/hasLinesSplitAndTotal'] && rootGetters['doc_viewer/incorrectSplitSum']) {
            errorsList.add(`Split amount not equal to 100% (${rootGetters['doc_viewer/splitSumPercentage']}%)`)
        }

        if (rootGetters['doc_viewer_settings/validateNoNewLineItems']) {
            rootGetters['doc_viewer/lines'].forEach((line, lineIdx) => {
                if (checkIsNewLine(line)) {
                    errorsList.add(`Line ${lineIdx + 1}: Nonexistent item selected`)
                }
            })
        }
        if (rootGetters['doc_viewer_settings/validateDocumentNotes'] &&
            !rootGetters['doc_viewer/hasAtLeastOneNonEmptyNote']) {
            errorsList.add(`At least one non-blank note must be added to the document.`)
        }

        const minAssigneesRequired = rootGetters['doc_viewer_settings/minAssigneesRequired']
        if (minAssigneesRequired > 0 && rootGetters['doc_viewer/getDocAssignedUserUuids'].length < minAssigneesRequired) {
            const assigneesPlural = minAssigneesRequired > 1 ? 'assignees' : 'assignee'
            errorsList.add(`At least ${minAssigneesRequired} ${assigneesPlural} must be selected`)
        }

        if (rootGetters['doc_viewer_settings/locationRequiredForEachLine']) {
            const eachLineHasLocation = rootGetters['doc_viewer/notDeletedLines'].every(l => _.get(l, 'location.uuid'))
            if (!eachLineHasLocation) {
                errorsList.add(`Location must be selected.`)
            }
        }

        if (rootGetters['doc_viewer_settings/subsidiaryRequiredForEachLine']) {
            const eachLineHasSubsidiary = rootGetters['doc_viewer/notDeletedLines'].every(l => _.get(l, 'subsidiary.uuid'))
            if (!eachLineHasSubsidiary) {
                errorsList.add(`Subsidiary must be selected.`)
            }
        }

        commit(DOC_METADATA_SET_ERRORS_LIST, [...errorsList])
        commit(DOC_METADATA_SET_IS_VALID, errorsList.size === 0)
    },

    [DOC_METADATA_BUILD_WARNINGS_LIST]: ({commit, rootGetters}) => {
        const warningsList = []
        if (rootGetters['doc_viewer/showInvalidBankDetailsAlert']) {
            warningsList.push(`Bank Account doesn't match`)
        }
        if (rootGetters['doc_viewer/allowAbnCheck'] && !rootGetters['doc_viewer/isValidAbn']) {
            warningsList.push(`ABN invalid`)
        }
        commit(DOC_METADATA_SET_WARNINGS_LIST, warningsList)
    }
}

export default {
    state,
    getters,
    mutations,
    actions,
    namespaced: true
}
